import React, {ChangeEvent, useCallback, useState} from 'react';

import {CONTEXTUAL_LINK_TITLE_INFO, CONTEXTUAL_LINK_VALUE_INFO} from 'src/modules/event-create/event-create-hovers';
import {FieldName} from 'src/modules/event-create/typedefs';
import {ChangeRequestContextualLink} from 'src/openapi/models/ChangeRequestContextualLink';

import {Button} from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Dropdown} from '@pb/kit-component-dropdown-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton, InfoIconButton} from '@pb/kit-component-icon-button';
import {Input} from '@pb/kit-component-input-v2';
import {Tooltip} from '@pb/kit-component-popups';

type Props = {
    currentLink?: ChangeRequestContextualLink,
    onClose: () => void,
    onChangeLink: (updatedLink: ChangeRequestContextualLink, link?: ChangeRequestContextualLink) => void,
    onRemoveLink?: (linkToDelete: ChangeRequestContextualLink) => void,
};

const CONTEXTUAL_LINK_TITLE_MAX_LENGTH = 30;
const CONTEXTUAL_LINK_TITLE_MIN_LENGTH = 3;

export const ContextualLinkModal = ({currentLink, onClose, onChangeLink, onRemoveLink}: Props) => {
    const [addedLink, setAddedLink] = useState<ChangeRequestContextualLink>({
        title: currentLink?.title ?? '',
        value: currentLink?.value ?? '',
    });

    const onClickExternalLink = useCallback(() => {
        window.open(addedLink.value);
    }, [addedLink]);

    const onApply = () => {
        onChangeLink(addedLink, currentLink);
    };

    const onRemove = () => {
        if (onRemoveLink && currentLink) {
            onRemoveLink(currentLink);
        }
    };

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = event.target;
        if (name === FieldName.CONTEXTUAL_LINK_TITLE) {
            setAddedLink({...addedLink, title: value});
        }

        if (name === FieldName.CONTEXTUAL_LINK_VALUE) {
            setAddedLink({...addedLink, value});
        }
    };

    return (
        <Group direction="v">
            <DialogHeader onClose={onClose}>
                <Tooltip
                    anchor={({anchorRef}) => (
                        <BasicIconButton
                            ref={anchorRef}
                            iconProps={{type: Icons.EXTERNAL_LINK}}
                            disabled={!addedLink.value}
                            onClick={onClickExternalLink}
                        />
                    )}>
                    External Link
                </Tooltip>
            </DialogHeader>
            <DialogMain>
                <Group direction="v" className="pdx-m">
                    <Item style={{width: '100%'}}>
                        <Group direction="v">
                            <Group gap="xs" alignItems="center">
                                <Item>Link Address</Item>
                                <Dropdown
                                    hoverEnabled={true}
                                    clickEnabled={false}
                                    maxWidth={500}
                                    anchor={({anchorRef, getAnchorProps}) => (
                                        <InfoIconButton {...getAnchorProps()} ref={anchorRef}/>
                                    )}>
                                    {CONTEXTUAL_LINK_VALUE_INFO}
                                </Dropdown>
                            </Group>
                            <Item style={{width: '100%'}}>
                                <Input
                                    name={FieldName.CONTEXTUAL_LINK_VALUE}
                                    value={addedLink.value}
                                    onChange={onChange}
                                />
                            </Item>
                        </Group>
                    </Item>
                    <Item style={{width: '100%'}}>
                        <Group direction="v">
                            <Group gap="xs" alignItems="center">
                                <Item>Display Text</Item>
                                <Dropdown
                                    hoverEnabled={true}
                                    clickEnabled={false}
                                    maxWidth={500}
                                    anchor={({anchorRef, getAnchorProps}) => (
                                        <InfoIconButton {...getAnchorProps()} ref={anchorRef}/>
                                    )}>
                                    {CONTEXTUAL_LINK_TITLE_INFO}
                                </Dropdown>
                            </Group>
                            <Item style={{width: '100%'}} className="pb-xs">
                                <Input
                                    name={FieldName.CONTEXTUAL_LINK_TITLE}
                                    maxLength={CONTEXTUAL_LINK_TITLE_MAX_LENGTH}
                                    value={addedLink.title}
                                    onChange={onChange}
                                />
                            </Item>
                        </Group>
                    </Item>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group justify="end" style={{width: '100%'}}>
                    {currentLink ? (
                        <Button colorVariant="ERROR" onClick={onRemove} variant="Type.SECONDARY">
                            Delete
                        </Button>
                    ) : null}
                    <Button
                        disabled={!addedLink.title || addedLink.title.length < CONTEXTUAL_LINK_TITLE_MIN_LENGTH}
                        onClick={onApply}
                        variant="Type.PRIMARY">
                        Apply
                    </Button>
                </Group>
            </DialogFooter>
        </Group>
    );
};
